<template>
  <div class="alarms-classification py-2">
    <b-row
      class="alarms-classification__alarms-search-field"
      no-gutters
      align-h="between"
      align-v="center"
    >
      <b-col
        class="my-3"
        sm="12"
        md="3"
      >
        <b-form-checkbox
          :checked="isAllAlarmsFromPageSelected"
          :disabled="!shouldShowAlarms"
          @change="markAllAlarms"
        > {{ checkboxTitleHandler }}
        </b-form-checkbox>
      </b-col>
      <b-col
        class="text-dark text-sm-center my-3 my-md-0"
        sm="12"
        md="auto"
        align-self="center"
      >
        <a
          v-if="showAssetsFiltered"
          class="alarms-classification__link-modal-label"
          @click="openFilteredAssetsModal"
        >
          <strong>{{ filteredAssetsTotal }}</strong> {{ filteredAssetsText }}
        </a>
        <span
          v-if="showAssetsFiltered && showSelectedField"
          class="alarms-classification__link-merge"
        >
          {{ ` ${$t('general.and')} ` }}
        </span>
        <a
          v-if="showSelectedField"
          class="alarms-classification__link-modal-label"
          @click="openSelectedAlarmsModal"
        >
          <strong>
            {{ selectedAlarmsLength }}
          </strong>
          {{ selectedAlarmsText }}
        </a>
      </b-col>
      <b-col
        class="my-3 p-0"
        xs="6"
        md="3"
        cols="12"
      >
        <s-search-input
          v-model="alarmName"
          v-s-trigger="searchTerm"
          :placeholder="$t('alarm-configurations.query.search-alarm')"
        />
      </b-col>
    </b-row>

    <loader
      v-show="loading"
      size="lg"
    />
    <s-empty-list v-if="emptyHandler" />
    <div v-if="shouldShowAlarms">
      <div
        v-if="!loading"
        class="alarms-classification__list"
      >
        <accordion-list
          class="alarms-classification__list-accordion"
          :modules="alarmsList"
          :selected-alarms="selectedAlarms"
          :filter="filter"
          @mark-all-from-module="markAllAlarmsFromModuleHandler"
          @mark-alarm="markAlarmHandler"
        />
      </div>
      <grid-paginator
        v-model="pagination.currentPage"
        class="alarms-classification__paginator pt-3"
        :per-page="pagination.perPage"
        :total-rows="pagination.totalRows"
        @input="paginate"
      />

      <b-row>
        <b-col
          class="text-right"
          cols="12"
        >
          <b-button
            class="alarm-configurations__edit-button"
            variant="primary"
            :disabled="isEditDisabled"
            @click="openEditModal"
          > {{ $t('actions.edit') }}
          </b-button>
        </b-col>
      </b-row>
    </div>
    <alarm-edit-modal
      v-model="showEditModal"
      ref="alarmEditModal"
      :selected-alarms="selectedAlarms"
      :edit-alarm-callback="editAlarmCallback"
      @clear-selected-alarms="clearSelectedAlarms"
    />
    <alarm-filtered-assets-modal
      v-model="filteredAssetsModalShow"
      :filter="filter"
    />
    <selected-alarms-modal
      v-model="selectedAlarmsModalShow"
      :selected-alarms="selectedAlarms"
    />
  </div>
</template>

<script>
import uniqBy from 'lodash/uniqBy';
import globalEventBus from '@/services/globalEventBus';
import GridPaginator from '@/components/lists/Paginator';
import Loader from '@/components/Loading';
import events from '@/domain/events/alarmsConfigurations';
import AccordionList from './AccordionList';
import AlarmEditModal from './modal/AlarmEditModal';
import SelectedAlarmsModal from './modal/SelectedAlarmsModal';
import AlarmFilteredAssetsModal from './modal/AlarmFilteredAssetsModal';


export default {
  name: 'AlarmsConfigurationList',
  props: {
    alarmsList: {
      type: Array,
      default: () => ([]),
    },
    loading: {
      type: [Boolean, Number],
    },
    pagination: {
      type: Object,
    },
    filter: {
      type: Object,
    },
    filteredAssetsTotal: {
      type: Number,
      default: 0,
    },
    editAlarmCallback: {
      type: Function,
    },
  },
  components: {
    Loader,
    GridPaginator,
    AlarmEditModal,
    AccordionList,
    SelectedAlarmsModal,
    AlarmFilteredAssetsModal,
  },
  data: () => ({
    selectedAlarms: [],
    lastRemoved: [],
    alarmName: null,
    showEditModal: false,
    filteredAssetsModalShow: false,
    selectedAlarmsModalShow: false,
  }),
  computed: {
    emptyHandler() {
      return !this.hasItems && !this.loading;
    },
    shouldShowAlarms() {
      return this.hasItems && !this.loading;
    },
    checkboxTitleHandler() {
      return this.isAllAlarmsFromPageSelected ? this.$t('alarm-configurations.query.uncheck-all-alarms') : this.$t('alarm-configurations.query.check-all-alarms');
    },
    showAssetsFiltered() {
      return this.filteredAssetsTotal > 0;
    },
    showSelectedField() {
      return (this.selectedAlarms || []).length >= 2;
    },
    hasItems() {
      return !!(this.alarmsList || []).length;
    },
    hasExhibitionPermission() {
      return this.$hasUserPermission('PER005');
    },
    isEditDisabled() {
      return !(this.selectedAlarms || []).length;
    },
    filteredAssetsText() {
      return this.$t('alarm-configurations.query.filtered-asset-s').toLowerCase();
    },
    selectedAlarmsLength() {
      return (this.selectedAlarms || []).length;
    },
    selectedAlarmsText() {
      return this.$t('alarm-configurations.query.selected-alarm-s').toLowerCase();
    },
    isAllAlarmsFromPageSelected() {
      if (!this.alarmsList.length) return false;
      const ids = (this.selectedAlarms || []).map(({ id }) => id);

      for (let index = 0; index < (this.alarmsList || []).length; index += 1) {
        const notFound = (this.alarmsList[index].alarms || [])
          .some(alarm => ids.indexOf(alarm.id) === -1);

        if (notFound) {
          return false;
        }
      }

      return true;
    },
  },
  created() {
    globalEventBus.on(events.editAlarm, this.openEditModal, this);
  },
  beforeDestroy() {
    globalEventBus.off(events.editAlarm, this.openEditModal, this);
  },
  methods: {
    searchTerm() {
      const { alarmName } = this;

      if (!alarmName) {
        this.$emit('search:alarmName', { alarmName: null });
        return;
      }
      this.$emit('search:alarmName', { alarmName });
    },
    paginate() {
      this.$emit('paginate', this.pagination);
    },
    openEditModal() {
      this.showEditModal = true;
      this.$refs.alarmEditModal.setEditOptions(this.selectedAlarms);
    },
    updateTotalBySelectedAlarms() {
      (this.selectedAlarms || []).forEach((alarm, index) => {
        this.selectedAlarms[index].total = (this.selectedAlarms || [])
          .filter(({ moduleId }) => moduleId === alarm.moduleId)
          .length;
      });
    },
    getMountedSelectedObject(alarm, module) {
      return {
        ...alarm,
        alarms: [alarm],
        moduleId: module.moduleId,
        moduleName: module.moduleName,
        total: module.total,
      };
    },
    unmarkAllAlarms() {
      this.alarmsList.forEach((module) => {
        module.alarms.forEach((alarm) => {
          this.selectedAlarms = this.selectedAlarms.filter(item => item.id !== alarm.id);
        });
      });
    },
    markAllAlarms(state) {
      if (!state) {
        this.unmarkAllAlarms();
        return;
      }

      const alarms = [...this.selectedAlarms];

      (this.alarmsList || []).forEach(module => (
        (module.alarms || [])
          .forEach(alarm => alarms
            .push(this.getMountedSelectedObject(alarm, module)))));

      this.selectedAlarms = uniqBy(alarms, 'id');
      this.updateTotalBySelectedAlarms();
    },
    addAlarmsByModule(module) {
      const listWithModuleId = (module.alarms || [])
        .map(alarm => (this.getMountedSelectedObject(alarm, module)));

      const alarms = [...this.selectedAlarms, ...listWithModuleId];
      return uniqBy(alarms, 'id');
    },
    removeAlarmsByModule(module) {
      const alarmsIds = (module.alarms || []).map(({ id }) => id);
      return (this.selectedAlarms || []).filter(
        item => !alarmsIds.includes(item.id),
      );
    },
    markAllAlarmsFromModuleHandler(state, module) {
      this.selectedAlarms = state
        ? this.addAlarmsByModule(module)
        : this.removeAlarmsByModule(module);

      this.updateTotalBySelectedAlarms();
    },
    markAlarmHandler(state, alarm, module) {
      if (state) {
        this.selectedAlarms.push(this.getMountedSelectedObject(alarm, module));
        return;
      }

      this.selectedAlarms = this.selectedAlarms.filter(({ id }) => id !== alarm.id);
      this.updateTotalBySelectedAlarms();
    },
    openFilteredAssetsModal() {
      this.filteredAssetsModalShow = true;
    },
    openSelectedAlarmsModal() {
      this.selectedAlarmsModalShow = true;
    },
    clearSelectedAlarms() {
      this.selectedAlarms = [];
    },
  },
};
</script>

<style lang="sass" scoped>
  .alarms-classification
    &__selected-label
      color: var(--color-gray)
    &__list
      max-height: 60vh
      overflow: auto
      border-top: 1px solid var(--color-background-dark)
      border-bottom: 1px solid var(--color-background-dark)
      &-accordion
        min-width: 820px
    &__paginator
      border-radius: .25rem
    &__alarms-search-field
      border-radius: .25rem
    &__link-merge
      color: var(--font-color-primary)
    &__link-modal-label
      text-decoration: underline
      color: var(--color-secondary)
      cursor: pointer
</style>
